import React, { useMemo, useEffect, useState, useCallback } from "react";
import GridTable from "./Components/gridTable";
import { getPlayParams } from "./Utils/functions";
import Container from "@mui/material/Container";
import Confetti from "react-confetti";
import WinnerDialog from "./Components/WinnerDialog";
import GameOptions from "./Components/GameOptions";
import { useCounter } from "./Hooks/useCounter";
import { useStats } from "./Hooks/useStats";
import { useInterstitialAd } from "./Hooks/useInterstitialAd";
import InterstitialOverlay from "./Components/InterstitialOverlay";
import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import BelowGameAd from "./Components/BelowGameAd";
import HeaderAd from "./Components/HeaderAd";
import ContactSection from "./Components/ContactSection";
import { Country, GameParams, PlayerProps, Team } from "./Types/types";
import { GameContext } from "./Context/GameContext";

const INITIAL_GAME_PARAMS: GameParams = {
  countries: [],
  teams: [],
};

const App: React.FC = () => {
  const location = useLocation();
  const tournament: string | null = new URLSearchParams(location.search).get(
    "tournament",
  );

  const [startPlay, setStartPlay] = useState<boolean>(false);
  const [gameParams, setGameParams] = useState<GameParams>(INITIAL_GAME_PARAMS);
  const [endGame, setEndGame] = useState<boolean>(false);
  const [isError, setIsError] = useState<string | false>(false);
  const [combinations, setCombinations] = useState<string[] | false>(false);
  const [guessedPlayers, setGuessedPlayers] = useState<
    Record<string, PlayerProps>
  >({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [surrendered, setSurrendered] = useState<boolean>(false);
  const [scoreAtSurrender, setScoreAtSurrender] = useState<number>(0);
  const [gameCompleted, setGameCompleted] = useState<boolean>(false);
  const { count, incrementCount, resetCounter } = useCounter(0);
  const { saveResult } = useStats();
  const [interstitialOpen, setInterstitialOpen] = useState(false);
  const { onRestart } = useInterstitialAd(() => setInterstitialOpen(true));

  const startGame = useCallback(() => {
    resetCounter();
    setGameParams(INITIAL_GAME_PARAMS);
    setEndGame(false);
    setIsError(false);
    setGuessedPlayers({});
    setSurrendered(false);
    setScoreAtSurrender(0);
    setGameCompleted(false);
    setIsLoading(true);
  }, [resetCounter, setEndGame]);

  // Restart the game — show interstitial overlay every 5th restart
  const handleRestartButton = useCallback(() => {
    onRestart();
    setStartPlay((prev) => !prev);
  }, [onRestart]);

  useEffect(() => {
    startGame();
    getPlayParams(tournament)
      .then((data) => {
        const { randomTeams, randomCountries } = data;
        setGameParams({
          countries: randomCountries,
          teams: randomTeams,
        });
        setCombinations(
          randomCountries.flatMap((country: Country) =>
            randomTeams.map((team: Team) => `${country.name}-${team.name}`),
          ),
        );
        setIsLoading(false);
      })
      .catch((err: Error) => {
        setIsError(err.message);
        setIsLoading(false);
      });
  }, [startPlay, tournament, startGame]);

  const memoizedGameParams = useMemo(
    () => ({
      countries: gameParams.countries,
      teams: gameParams.teams,
    }),
    [gameParams],
  );

  // Check if the player won the game
  useEffect(() => {
    if (Array.isArray(combinations) && combinations.length === 0) {
      setEndGame(true);
      setGameCompleted(true);
      saveResult(
        tournament ?? "Unknown",
        gameParams.countries.length * gameParams.teams.length
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combinations]);

  // Auto-dismiss error messages
  useEffect(() => {
    if (!isError) return;
    const timer = setTimeout(() => setIsError(false), 2500);
    return () => clearTimeout(timer);
  }, [isError]);

  const contextValue = {
    combinations,
    setCombinations,
    setIsError,
    tournament,
    guessedPlayers,
    setGuessedPlayers,
    endGame,
    count,
    incrementCount,
    surrendered,
    setSurrendered,
    scoreAtSurrender,
    setScoreAtSurrender,
    gameCompleted,
    saveResult,
  };

  return (
    <GameContext.Provider value={contextValue}>
      <>
        <Container
          className="App-container"
          maxWidth="md"
          sx={{
            position: "relative",
            paddingTop: { xs: "5rem", sm: "3.5rem", md: "3rem" },
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: { xs: 3, md: 4 },
          }}
        >
          <HeaderAd />

          {/* Main Grid Table */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              px: { xs: 1, sm: 2 },
            }}
          >
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "280px",
                }}
              >
                <CircularProgress size={56} sx={{ color: "#f2b705" }} />
              </Box>
            ) : (
              <GridTable gameParams={memoizedGameParams} />
            )}
          </Box>

          {/* Progress indicator */}
          {!isLoading && gameParams.countries.length > 0 && (
            <Box sx={{ width: "100%", maxWidth: "sm", px: { xs: 2, sm: 4 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.75,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    letterSpacing: "0.05em",
                  }}
                >
                  Progress
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#f2b705", fontWeight: 700 }}
                >
                  {surrendered ? scoreAtSurrender : Object.keys(guessedPlayers).length} /{" "}
                  {gameParams.countries.length * gameParams.teams.length}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={
                  ((surrendered ? scoreAtSurrender : Object.keys(guessedPlayers).length) /
                    (gameParams.countries.length * gameParams.teams.length)) *
                  100
                }
                sx={{
                  borderRadius: 4,
                  height: 6,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  "& .MuiLinearProgress-bar": { backgroundColor: "#f2b705" },
                }}
              />
            </Box>
          )}

          {/* Game Options */}
          <Box
            sx={{
              width: "100%",
            }}
          >
            <GameOptions handleRestartButton={handleRestartButton} />
          </Box>

          {/* Error Message */}
          {isError && (
            <div id="error-message">
              <p style={{ margin: 0 }}>{isError}</p>
            </div>
          )}
          <Box sx={{ width: "100%", textAlign: "center", mb: 2 }}>
            <BelowGameAd tournament={tournament} />
          </Box>
          <ContactSection />
        </Container>

        {endGame && (
          <>
            <Confetti />
            <WinnerDialog
              handleRestartButton={handleRestartButton}
              count={count}
              setEndGame={setEndGame}
            />
          </>
        )}

        <InterstitialOverlay
          open={interstitialOpen}
          onClose={() => setInterstitialOpen(false)}
        />
      </>
    </GameContext.Provider>
  );
};

export default App;
